import React, { useCallback, useRef, useState } from "react"
import { useFormikContext } from "formik"
import { Button, Grid, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import {
    CreateUpdateProductFormValues,
    ProductStockInfo,
    StockTransactionType,
    StockAdjustmentType,
    StockPopoverFormValues,
    StockChangeReason,
    ADJUSTMENT_TO_TRANSACTION,
} from "../../productsTypes"
import { StockPopover } from "./StockPopover"

const useStyles = makeStyles()((theme) => ({
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    stockButton: {
        textTransform: "none",
        borderRadius: "0.5rem",
        fontWeight: 500,
        padding: "0.125rem 0.75rem",
        fontSize: theme.typography.body2.fontSize,
        color: "#2196F3",
        borderColor: "#2196F3",
    },
}))

const noop = () => {}

export function StockField() {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateUpdateProductFormValues>()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const currentStockRef = useRef<ProductStockInfo>({
        inventory: { total: 0, sold: 0, reserved: 0, available: 0 },
    })
    const initialInventoryRef = useRef<StockPopoverFormValues | undefined>()

    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
        currentStockRef.current = {
            inventory: { total: values.stock?.total ?? 0, sold: 0, reserved: 0, available: 0 },
        }
        initialInventoryRef.current = values.stock?.inventory
            ? {
                  type:
                      values.stock.inventory.stockUpdate.transactionType === StockTransactionType.INCREASE
                          ? StockAdjustmentType.INBOUND
                          : StockAdjustmentType.OUTBOUND,
                  quantityDelta: String(values.stock.inventory.stockUpdate.quantityDelta),
                  reason: values.stock.inventory.stockUpdate.reason,
                  note: values.stock.inventory.stockUpdate.note ?? "",
              }
            : undefined
        setAnchorEl(event.currentTarget)
    }

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleInventoryValuesChange = useCallback(
        (popoverValues: StockPopoverFormValues) => {
            const qty = Number(popoverValues.quantityDelta)
            const hasValidValues = popoverValues.quantityDelta && !isNaN(qty) && qty > 0 && popoverValues.reason

            setFieldValue(
                "stock.inventory",
                hasValidValues
                    ? {
                          stockUpdate: {
                              transactionType: ADJUSTMENT_TO_TRANSACTION[popoverValues.type],
                              quantityDelta: qty,
                              reason: popoverValues.reason as StockChangeReason,
                              note: popoverValues.note || undefined,
                          },
                      }
                    : undefined,
            )
        },
        [setFieldValue],
    )

    const stockTotal = values.stock?.total ?? 0

    return (
        <>
            <Grid className={classes.row}>
                <LocalizedStrict id="product-stock-total-stock-label">
                    <Typography variant="body1">Total stock:</Typography>
                </LocalizedStrict>
                <LocalizedStrict id="product-stock-total-stock-items" vars={{ count: stockTotal }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faPen} />}
                        onClick={handleOpenPopover}
                        className={classes.stockButton}
                    >
                        {`${stockTotal} items`}
                    </Button>
                </LocalizedStrict>
            </Grid>

            {anchorEl && (
                <StockPopover
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    productId={0}
                    currentStock={currentStockRef.current}
                    onStockUpdate={noop}
                    formMode
                    initialInventoryValues={initialInventoryRef.current}
                    onInventoryValuesChange={handleInventoryValuesChange}
                />
            )}
        </>
    )
}
